import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { ExportDialogComponent } from '@core/components/export-dialog/export-dialog.component';
import { ExportDialogComponentInjectedData, ExportDialogTypeEnum } from '@core/components/export-dialog/export-dialog.types';
import { SidenavService } from '@core/services/sidenav.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Group } from '@interfaces';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogGroupsComponent } from './dialogs/dialog-groups.component';
import { FilterGroupsComponent } from './filter/filter-groups.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { GroupService } from './services/groups-api.service';


@Component({
  selector: 'groups',
  templateUrl: './groups.component.html',
  animations: [fuseAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class GroupsComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true })
  private paginator: MatPaginator;

  @ViewChild(MatSort, { static: true })
  private sort: MatSort;

  @ViewChild(FilterGroupsComponent, { static: true })
  public filterGroupsComponent: FilterGroupsComponent;

  @ViewChild('datatable', { read: ElementRef, static: true })
  public datatable: ElementRef<HTMLDivElement>;

  public displayedColumns: string[] = ['name', 'description', 'createdAt', 'edit'];
  public groupDetailSidebarName = 'groupDetail';
  public groups: Group[] = [];
  public totalCount: Number;
  public filtered = false;
  public loading = true;

  public groupsReload = new Subject<any>();
  private subscriptions = new Subject<void>();

  public hasEditGroupPermission = this.authenticationService.hasPermission('groups-edit');
  public hasCreateGroupPermission = this.authenticationService.hasPermission('groups-create');
  public hasCreateExportPermission = this.authenticationService.hasPermission('exports-create');

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private authenticationService: AuthenticationService,
    private progressBarService: FuseProgressBarService,
    private sidebarService: FuseSidebarService,
    public sidenavService: SidenavService,
    private groupsService: GroupService,
    private matDialog: MatDialog,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.sort.active = 'name';
    this.sort.direction = 'asc';

    merge(
      this.sort.sortChange,
      this.paginator.page,
      this.filterGroupsComponent.filterChanged.pipe(
        tap(() => this.paginator.firstPage())
      ),
      this.groupsReload
    ).pipe(
      debounceTime(500),
      takeUntil(this.subscriptions),
      switchMap(() => {
        this.progressBarService.show();
        this.sort.direction = this.sort.direction || 'asc';

        const options = {
          orderBy: `${this.sort.active}_${this.sort.direction}`,
          offset: this.paginator.pageIndex * this.paginator.pageSize,
          limit: this.paginator.pageSize
        };

        this.loading = true;
        return this.groupsService.loadData({ where: this.filterGroupsComponent.filterChanged.value, options });
      }))
      .subscribe((result) => {
        this.progressBarService.hide();
        this.datatable.nativeElement.scrollTo({ top: 0 });
        this.groups = result.items;
        this.totalCount = result.totalCount;
        this.filtered = result.filtered;
        this.loading = false;
      });
  }

  public openExportDialog(): void {
    this.matDialog.open(ExportDialogComponent, {
      panelClass: 'export-dialog',
      autoFocus: false,
      data: {
        type: ExportDialogTypeEnum.groups,
        limit: 10000,
        total: this.totalCount,
        where: this.filterGroupsComponent.filterChanged.value
      } as ExportDialogComponentInjectedData
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  reload(): void {
    this.groupsReload.next();
  }

  toggleFilterGroupsSidebar(): void {
    this.sidebarService.getSidebar('filterGroups').toggleOpen();
  }

  async openDialog(groupId?: string): Promise<void> {
    this.progressBarService.show();

    let group: Group = {
      description: null,
      name: null,
      users: [],
      permissions: []
    };

    if (groupId) {
      group = await this.groupsService.getGroupById(groupId);
    }

    const groupDialog = this.matDialog.open(DialogGroupsComponent, {
      panelClass: ['dialog-groups'],
      data: {
        group,
        isViewMode: groupId && !this.hasEditGroupPermission
      }
    });

    this.progressBarService.hide();

    await groupDialog.afterClosed()
      .toPromise()
      .then((reload) => {
        if (reload) {
          this.reload();
        }
      });
  }

  clearFilters(): void {
    this.filtered = false;
    this.filterGroupsComponent.clearFilters();
  }
}
